import React, { useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import { RemoveScrollBar } from "react-remove-scroll-bar"
import throttle from "lodash/throttle"
import { trailingSlashIt } from "@bufox/js-utils"
import styled from "styled-components"
import { BiMenu as DefaultMenuIcon } from "react-icons/bi"
import { GrClose as DefaultCloseIcon } from "react-icons/gr"
import { MdExpandMore as DefaultExpandButtonIcon } from "react-icons/md"
const DefaultCollapseButtonIcon = styled(DefaultExpandButtonIcon)`
  transform: rotate(180deg);
`
const DefaultLink = props => {
  return (
    <a href={props.to} title={props.title}>
      {props.children}
    </a>
  )
}

DefaultLink.propTypes = {
  to: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.node,
}

DefaultLink.defaultProps = {
  to: "#",
  title: "",
  children: null,
}

const isActiveLink = (entry, path) => {
  /* Only works for relative links */
  if (entry.url?.startsWith("/")) {
    if (entry && trailingSlashIt(entry.url) === trailingSlashIt(path)) {
      return true
    }
  }
  return false
}

const DryMenu = props => {
  const {
    className = "",
    closeButtonIcon: inputCloseButtonIcon,
    collapseButtonIcon: inputCollapseButtonIcon,
    enableSticky,
    expandButtonIcon: inputExpandButtonIcon,
    linkComponent: Link,
    menuId,
    mobileAuxiliaryMenu,
    mobileFeaturedComponent,
    path,
    primaryMenu,
    scrollThrottlingMs,
    stickyOffset,
    toggleMenuIcon: inputToggleMenuIcon,
  } = props

  const [navStateOpen, setNavStateOpen] = useState(false)

  const [sticky, setSticky] = useState(false)
  const [stickyClass, setStickyClass] = useState("")
  const [scrollPosition, _setScrollPosition] = useState(null)
  const scrollPositionRef = useRef(scrollPosition)
  const setScrollPosition = data => {
    scrollPositionRef.current = data
    _setScrollPosition(data)
  }

  let toggleMenuIcon = null
  if (inputToggleMenuIcon) {
    toggleMenuIcon = inputToggleMenuIcon
  } else {
    toggleMenuIcon = <DefaultMenuIcon></DefaultMenuIcon>
  }

  let closeButtonIcon = null
  if (inputCloseButtonIcon) {
    closeButtonIcon = inputCloseButtonIcon
  } else {
    closeButtonIcon = <DefaultCloseIcon></DefaultCloseIcon>
  }

  let expandButtonIcon = null
  if (inputExpandButtonIcon) {
    expandButtonIcon = inputExpandButtonIcon
  } else {
    expandButtonIcon = <DefaultExpandButtonIcon></DefaultExpandButtonIcon>
  }

  let collapseButtonIcon = null
  if (inputCollapseButtonIcon) {
    collapseButtonIcon = inputCollapseButtonIcon
  } else {
    collapseButtonIcon = <DefaultCollapseButtonIcon></DefaultCollapseButtonIcon>
  }

  const evaluateSticky = throttle(() => {
    if (window.pageYOffset > stickyOffset) {
      const scrollPosition = scrollPositionRef.current
      const pageYOffset = window.pageYOffset
      setSticky(true)
      if (scrollPosition && scrollPosition !== pageYOffset) {
        if (pageYOffset < scrollPosition) {
          setStickyClass("sticky sticky-visible")
        } else {
          setStickyClass("sticky sticky-hidden")
        }
      }
    } else {
      setSticky(false)
      setStickyClass("")
    }
    setScrollPosition(pageYOffset)
  }, scrollThrottlingMs)

  useEffect(() => {
    if (enableSticky) {
      window.addEventListener("scroll", evaluateSticky)
      return () => {
        window.removeEventListener("scroll", evaluateSticky)
      }
    }
    return null
  }, [])

  return (
    <div className={`${className}${stickyClass ? ` ${stickyClass}` : ""}`}>
      {navStateOpen ? <RemoveScrollBar /> : null}
      <div className="navbar">
        <label className="toggle-menu-panel" htmlFor={`toggle-${menuId}`}>
          <div className={`menu-icon${navStateOpen ? " open" : " closed"}`}>
            <span className="open-menu-icon">{toggleMenuIcon}</span>
            <span className="close-menu-icon">{closeButtonIcon}</span>
          </div>
        </label>
        {mobileFeaturedComponent ? (
          <div className="mobile-featured-component">
            {mobileFeaturedComponent}
          </div>
        ) : null}
      </div>
      <div className={`menu-container`}>
        <input
          id={`toggle-${menuId}`}
          type="checkbox"
          className="toggle-menu-panel"
          onChange={e => {
            setNavStateOpen(e.target.checked)
          }}
        />
        <nav id={`${menuId}`}>
          {
            /**
             * Recursive menu entries here
             */
            primaryMenu?.length && (
              <RecursiveMenu
                className={`${menuId}-level-${1}`}
                collapseButtonIcon={collapseButtonIcon}
                entries={primaryMenu}
                expandButtonIcon={expandButtonIcon}
                level={1}
                linkComponent={Link}
                menuId={`${menuId}`}
                path={path}
                uniqueName={menuId}
              />
            )
          }
          {mobileAuxiliaryMenu.length ? (
            <ul className="auxiliary-nav">
              {mobileAuxiliaryMenu.map(auxiliaryEntry => (
                <li
                  key={`mobile-menu-translation-${auxiliaryEntry.language.code}`}
                >
                  <Link to={auxiliaryEntry.uri}>
                    {auxiliaryEntry.language.code}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            ""
          )}
        </nav>
      </div>
    </div>
  )
}

DryMenu.propTypes = {
  className: PropTypes.string,
  closeButtonIcon: PropTypes.element,
  collapseButtonIcon: PropTypes.element,
  enableSticky: PropTypes.bool,
  expandButtonIcon: PropTypes.element,
  toggleMenuIcon: PropTypes.element,
  linkComponent: PropTypes.elementType,
  menuId: PropTypes.string,
  mobileAuxiliaryMenu: PropTypes.array,
  mobileFeaturedComponent: PropTypes.element,
  path: PropTypes.string,
  primaryMenu: PropTypes.array,
  scrollThrottlingMs: PropTypes.number,
  stickyOffset: PropTypes.number,
}
DryMenu.defaultProps = {
  className: "",
  enableSticky: true,
  linkComponent: DefaultLink,
  menuId: "menu",
  mobileFeaturedComponent: null,
  path: null,
  primaryMenu: [],
  scrollThrottlingMs: 100,
  stickyOffset: 500,
  mobileAuxiliaryMenu: [],
}

const RecursiveMenu = ({
  className,
  collapseButtonIcon,
  expandButtonIcon,
  entries,
  level,
  linkComponent: Link = DefaultLink,
  menuId,
  path,
}) => {
  entries = entries.filter(entry => entry.slug)
  const [entriesThatCanBeExpanded] = useState(
    entries.reduce((currentEntries, entry) => {
      if (entry?.children?.length) {
        currentEntries[entry.slug] = {}
      }
      return currentEntries
    }, {})
  )

  const canBeExpanded = slug => {
    return slug in entriesThatCanBeExpanded
  }

  return (
    <ul className={className}>
      {entries.map((entry, i) => (
        <li
          className={`entry${canBeExpanded(entry.slug) ? " has-children" : ""}`}
          key={`entry-${entry.slug}`}
        >
          {entry.url ? <Link to={entry.url}>{entry.label}</Link> : entry.label}
          {canBeExpanded(entry.slug) ? (
            <>
              <input
                id={`${menuId}-${level}-${entry.slug}-${i}`}
                type="checkbox"
                className="toggle-sub-menu"
              />
              <label
                className="toggle-submenu-button"
                htmlFor={`${menuId}-${level}-${entry.slug}-${i}`}
              >
                <span className="expand">{expandButtonIcon}</span>
                <span className="collapse">{collapseButtonIcon}</span>
              </label>
              <RecursiveMenu
                collapseButtonIcon={collapseButtonIcon}
                className={`${menuId}-level-${level + 1}`}
                entries={entry.children}
                expandButtonIcon={expandButtonIcon}
                key={`submenu-entry-${entry.slug}`}
                level={level + 1}
                linkComponent={Link}
                menuId={`${menuId}`}
                path={path}
              ></RecursiveMenu>
            </>
          ) : (
            ``
          )}
        </li>
      ))}
    </ul>
  )
}

RecursiveMenu.propTypes = {
  className: PropTypes.string,
  collapseButtonIcon: PropTypes.element,
  enableSticky: PropTypes.bool,
  entries: PropTypes.array,
  expandButtonIcon: PropTypes.element,
  level: PropTypes.number,
  linkComponent: PropTypes.elementType,
  menuId: PropTypes.string,
  path: PropTypes.string,
  scrollThrottlingMs: PropTypes.number,
  stickyOffset: PropTypes.number,
  uniqueName: PropTypes.string,
}

const StyledDryMenu = styled(DryMenu)`
  @media only screen and (max-width: ${props => {
      return `${props.lgScreen.replace("px", "") - 1}px`
    }}) {
    width: 100%;
    background: white;
    position: fixed;
    z-index: 1;

    &.sticky {
      top: 0;
      transition: top 0.5s linear;
      position: fixed;
      width: 100%;
      z-index: 50;
    }

    &.sticky-visible {
      top: 0;
    }

    &.sticky-hidden {
      top: -5rem;
    }

    // <Component><div class="navbar" ...
    .navbar {
      box-shadow: 0 2px 6px rgb(136 136 136 / 26%);
      display: flex;
      padding: 0 0 0 1rem;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 4rem;
      display: flex;
      justify-content: space-between;
    }

    // hamburger menu icon
    .menu-icon {
      line {
        stroke: black;
      }
      .open-menu-icon,
      .close-menu-icon {
        display: block;
        transition: opacity 500ms ease-out;
      }
      .open-menu-icon {
        font-size: 2rem;
      }
      .close-menu-icon {
        font-size: 1rem;
      }
      &.open {
        .open-menu-icon {
          opacity: 0;
          visibility: hidden;
          height: 0;
        }
        .close-menu-icon {
          opacity: 1;
          visibility: visible;
        }
      }
      &.closed {
        .open-menu-icon {
          opacity: 1;
          visibility: visible;
        }
        .close-menu-icon {
          opacity: 0;
          visibility: hidden;
          height: 0;
        }
      }
    }

    .toggle-menu-panel {
      display: block;
    }

    // inside <div class="menu-container" and beside <input class="toggle-menu-panel"
    nav {
      visibility: hidden;
      height: 0;
      overflow: hidden;
      transition: height 500ms ease-out, visibility 500ms ease-out;
      overflow: auto;
    }

    ul {
      width: 100%;
    }

    .mobile-featured-component {
      flex: 1;
      padding-right: 0.5rem;
    }

    .toggle-submenu-button {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      font-size: 2rem;
      color: black;
      svg {
        transition: transform 500ms ease-out;
      }
    }

    input[type="checkbox"] {
      display: none;

      &.toggle-menu-panel:checked ~ nav {
        visibility: visible;
        height: 100vh;
      }
    }

    .entry {
      display: flex;
      flex-wrap: wrap;
      position: relative;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      a {
        max-height: 0;
        display: flex;
        align-items: center;
        width: 80%;
        padding: 0;
        opacity: 0;
        text-overflow: ellipsis;
        line-height: 1.5rem;
        overflow: hidden;
        display: flex;
        line-clamp: 4;
        flex-direction: column;
        align-items: stretch;
      }
      .entry {
        padding-left: 1rem;
      }
      > label {
        width: 20%;
        padding: 0;
      }
    }

    .menu-level-1 {
      padding: 1rem;
      > .entry {
        > a,
        label {
          opacity: 1;
          max-height: 7rem;
          padding: 1rem 0;
        }
        input[type="checkbox"] {
          ~ ul > .entry {
            > {
              a {
                transition: opacity 500ms ease-out, padding 500ms ease-out,
                  max-height 500ms ease-out;
              }
              label {
                transition: opacity 500ms ease-out, padding 500ms ease-out,
                  max-height 500ms ease-out, transform 500ms ease-out;
              }
            }
          }
          &:not(:checked) {
            ~ ul > {
              .entry {
                a,
                label {
                  max-height: 0 !important;
                  overflow: hidden !important;
                  padding: 0 !important;
                  opacity: 0 !important;
                }
              }
            }
            ~ .toggle-submenu-button {
              .expand {
                visibility: visible;
              }
              .collapse {
                visibility: hidden;
                width: 0;
              }
            }
          }
          &:checked {
            ~ ul > .entry {
              > {
                a,
                label {
                  max-height: 7rem;
                  opacity: 1;
                  padding: 1rem 0;

                  /* visibility: visible; */
                  /* transition: opacity 500ms ease-out, max-height 500ms ease-out; */
                }
              }
            }
            ~ .toggle-submenu-button {
              .expand {
                visibility: hidden;
                width: 0;
              }
              .collapse {
                visibility: visible;
              }
            }
          }
        }
      }
    }

    .auxiliary-nav {
      padding: 2rem 1rem 4rem 1rem;
      font-size: 1.4rem;
      display: flex;
      li {
        padding: 0 0.7rem;
        position: relative;

        &:after {
          position: absolute;
          height: 1.4rem;
          width: 1px;
          border: 1px solid black;
          right: 0;
          top: 0;
          bottom: 0;
          margin-top: auto;
          margin-bottom: auto;
          display: block;
          content: "";
        }
        &:first-child {
          padding-left: 0;
        }
        &:last-child:after {
          display: none;
        }
      }
    }
  }

  @media only screen and (min-width: ${props => props.smScreen}) {
  }
  // Medium screens
  @media only screen and (min-width: ${props => props.mdScreen}) {
  }
  // Large screens
  @media only screen and (min-width: ${props => props.lgScreen}) {
    input[type="checkbox"] {
      display: none;
    }
    .search-box {
      position: absolute;
      top: 3rem;
      right: 3rem;
    }
    .toggle-menu-panel {
      display: none;
    }

    // All entries. It includes menu-level-1 entries as well
    .entry {
      font-size: 1.15rem;
      text-transform: uppercase;
      position: relative;
      a {
        display: block;
      }
      input[type="checkbox"] {
        display: none;
      }
    }

    .auxiliary-nav {
      display: none;
    }

    .mobile-featured-component {
      display: none;
    }

    .menu-level-1 {
      width: 100%;
      text-align: center;
      color: #fff;
      justify-content: center;
      padding: 2rem 1rem;

      // All levels uls contained in menu-level-1
      ul {
        width: 100%;
        background: #efefef;
        color: black;
        z-index: 1;
        text-align: left;
      }
      // .menu-level-1 > .entry: Only first level entries
      > .entry {
        text-align: center;
        display: inline-block;
        padding: 0.5rem 1rem;
        > .toggle-submenu-button {
          display: none;
        }
        // second level uls (menu-level-2)
        > ul {
          position: absolute;
          top: 100%;
          left: 0;
          overflow: hidden;
          max-height: 0;
          transition: max-height 500ms ease-out;
          padding: 2rem 0.5rem 2rem 1rem; /* pr 0.5 and not 1 
          to compensate scrollbar space. 
          Indeed, even without scrollbar the space is preserved. */

          // all entries >= 2nd level
          .entry {
            display: flex;
            flex-wrap: wrap;
            position: relative;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            font-size: 0.9rem;
            overflow: hidden;

            a {
              width: 90%;
              display: flex;
              align-items: center;
              transition: opacity 500ms ease-out, height 500ms ease-out;
            }
            &.has-children {
              a {
                width: 75%;
              }
            }
            .toggle-submenu-button {
              font-size: 2rem;
              color: var(--gray6);
              height: 0;
              width: 25%;
              display: flex;
              justify-content: center;
              align-items: center;
              cursor: pointer;
              transition: opacity 500ms ease-out, height 500ms ease-out,
                transform 500ms ease-out;
              svg {
                transition: transform 500ms ease-out;
              }
            }
            // expand/collapse section
            > input[type="checkbox"] {
              ~ ul > li > {
                a,
                label {
                  transition: opacity 500ms ease-out, padding 500ms ease-out,
                    max-height 500ms ease-out;
                  max-height: 0;
                  opacity: 0;
                }
              }
              &:not(:checked) {
                ~ ul .entry > {
                  a,
                  label {
                    max-height: 0 !important;
                    overflow: hidden !important;
                    opacity: 0;
                    padding: 0 !important;
                  }
                }
                ~ .toggle-submenu-button {
                  .expand {
                    visibility: visible;
                  }
                  .collapse {
                    visibility: hidden;
                    width: 0;
                  }
                }
              }
              &:checked {
                ~ ul > .entry {
                  > a {
                    opacity: 1;
                    max-height: 8rem;
                    padding: 0.5rem 0; // padding contributes to height
                  }
                  > label {
                    opacity: 1;
                    max-height: 8rem;
                  }
                }
                ~ .toggle-submenu-button {
                  .expand {
                    visibility: hidden;
                    width: 0;
                  }
                  .collapse {
                    visibility: visible;
                  }
                }
                ~ label {
                  svg {
                    transform: rotate(180deg);
                  }
                }
              }
            }
          }
        }
        &:hover {
          > a {
            color: gray;
          }
          // hovering on first level entries
          > ul {
            overflow: scroll; // not using auto to avoid flickering while transitioning on hovering
            // Scrollbar: https://www.w3schools.com/howto/howto_css_custom_scrollbar.asp
            ::-webkit-scrollbar {
              width: 0.5rem;
            }
            /* Track */
            ::-webkit-scrollbar-track {
              background: #efefef;
            }

            /* Handle */
            ::-webkit-scrollbar-thumb {
              background: gray;
            }

            /* Handle on hover */
            ::-webkit-scrollbar-thumb:hover {
              background: #555;
            }

            max-height: 25rem;
            > .entry {
              // all entries = 2nd level. They are the only allowed to show if hovering on a parent entry
              > a {
                display: flex;
                align-items: center;
                padding: 0.5rem 0; // padding contributes to height
              }
              .toggle-submenu-button {
                position: relative;
              }
            }
          }
        }
        &:not(:hover) {
          > ul {
            overflow: hidden !important;
            padding: 0 !important;
            height: 0 !important;
          }
        }
        // first level entries separator
        &:after {
          content: "";
          position: absolute;
          right: 0;
          width: 2px;
          height: 1rem;
          top: 0;
          bottom: 0;
          margin-top: auto;
          margin-bottom: auto;
          background-color: black;
        }
        &:last-child {
          &:after {
            display: none;
          }
        }
      }
    }
  }

  // Extra Large screens
  @media only screen and (min-width: ${props => props.xlScreen}) {
  }
  // Wider screens
  @media only screen and (min-width: ${props => props["2xlScreen"]}) {
  }
`

StyledDryMenu.defaultProps = {
  smScreen: "640px",
  mdScreen: "768px",
  lgScreen: "1024px",
  xlScreen: "1280px",
  "2xlScreen": "1536px",
}

export default StyledDryMenu
export { StyledDryMenu }
